import React, { useLayoutEffect, useMemo } from 'react';

import {
  CustomizedButton,
  CustomizedDialog,
  CustomizedTooltip,
  CustomizedTypography
} from '@components/Customized';

import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Select,
  useMediaQuery
} from '@material-ui/core';
import { Create } from '@material-ui/icons';
import useStyles from '../useStyles';
import OutlinedTextField, {
  CurrencyFormatInput
} from '@routes/Common/OutlinedTextField';
import { UploadedDrawingFileType } from '@modules/request';
import { ServiceTypes } from '../RequestForQuotationUpload';

import {
  selectCastingOptions,
  selectCNCOptions,
  selectMoldingOptions,
  selectPrintingOptions,
  selectSheetMetalOptions
} from './SelectOptionsTypes';

import { requestEnumToString } from '@utils/enumToString';
import {
  Controller,
  useForm,
  useFormContext as useFormContextFromCotainer
} from 'react-hook-form';

import {
  CapaQuotationDetailedMaterialType,
  CapaQuotationDetailedServiceType,
  CapaQuotationMaterial,
  CapaQuotationRecommendation,
  CapaQuotationServiceType
} from '@generated/graphql';

import OptionRecommendationBox from './OptionRecommendationBox';
import { colorSet, theme } from '@components/Provider';
import { Close } from '@carbon/icons-react';
import { Enum_Action_Button_Enum } from '@hooks/useUserActionLog';

interface Props extends ServiceTypes {
  open: boolean;
  selectedFileInfo: UploadedDrawingFileType | undefined;
  onClose: () => void;
  handleUpdateRfqFile: (payload: UploadedDrawingFileType) => void;
  isMultiple: boolean;
  handleBatchUpdateRfqFile: (payload: UploadedDrawingFileType) => void;
  uploadedFilesError: string[];
  toolTipOpen: boolean;
  onCloseToolTip: () => void;
}

const OptionSelectDialog = ({
  serviceType,
  detailedServiceType,
  selectedFileInfo,
  open,
  onClose,
  handleUpdateRfqFile,
  isMultiple,
  handleBatchUpdateRfqFile,
  uploadedFilesError,
  toolTipOpen,
  onCloseToolTip
}: Props) => {
  const methods = useForm<UploadedDrawingFileType>();

  const { clearErrors } = useFormContextFromCotainer();

  const { control, errors, watch, reset, setValue, handleSubmit } = methods;

  const classes = useStyles();

  const matches = useMediaQuery(theme.breakpoints.down('xs'));

  const hasDetailedMaterial = [
    CapaQuotationServiceType.Cnc,
    CapaQuotationServiceType.Inj
  ].includes(serviceType);

  const material = watch('material');

  const detailedMaterial = hasDetailedMaterial
    ? watch('detailedMaterial')
    : undefined;

  const isRecommendServiceType =
    detailedServiceType === CapaQuotationDetailedServiceType.Recommend;

  const selectedMaterialRecommend =
    material === CapaQuotationMaterial.Recommend;

  const hasEtc = material?.includes('ETC') || detailedMaterial?.includes('ETC');

  const hasRecommendation =
    material?.includes('RECOMMEND') ||
    detailedMaterial?.includes('RECOMMEND') ||
    isRecommendServiceType;

  const materialOptions =
    serviceType === 'CNC'
      ? selectCNCOptions.material
      : serviceType === 'PRINTING'
      ? selectPrintingOptions.material[detailedServiceType]
      : serviceType === 'INJ'
      ? selectMoldingOptions.material
      : serviceType === 'SHEET_METAL'
      ? selectSheetMetalOptions.material[detailedServiceType]
      : serviceType === 'CASTING'
      ? selectCastingOptions.material[detailedServiceType]
      : [];

  const detailedMaterialOptions =
    serviceType === 'CNC'
      ? selectCNCOptions.detailedMaterial[material as CapaQuotationMaterial]
      : serviceType === 'INJ'
      ? selectMoldingOptions.detailedMaterial[material as CapaQuotationMaterial]
      : undefined;

  const onSubmit = handleSubmit((data) => {
    clearErrors(selectedFileInfo?.fileId);
    const isEtc =
      data?.material === CapaQuotationMaterial.Etc ||
      data?.detailedMaterial === CapaQuotationDetailedMaterialType.Etc;

    const materialRecommend =
      data?.material === CapaQuotationMaterial.Recommend;

    const detailedMaterialRecommend =
      data?.detailedMaterial === CapaQuotationDetailedMaterialType.Recommend;

    if (isRecommendServiceType) {
      handleUpdateRfqFile({
        ...selectedFileInfo,
        ...data,
        material: CapaQuotationMaterial.Recommend
      });

      return onClose();
    }

    if (isEtc) {
      handleUpdateRfqFile({
        ...selectedFileInfo,
        ...data,
        recommendation: undefined
      });

      return onClose();
    }

    if (materialRecommend) {
      handleUpdateRfqFile({
        ...selectedFileInfo,
        ...data,
        detailedMaterial: undefined,
        etc: undefined
      });

      return onClose();
    }

    if (detailedMaterialRecommend) {
      handleUpdateRfqFile({ ...selectedFileInfo, ...data, etc: undefined });

      return onClose();
    }

    handleUpdateRfqFile({
      ...selectedFileInfo,
      ...data,
      etc: undefined,
      recommendation: undefined
    });

    return onClose();
  });

  const logId = useMemo(() => {
    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Button_Enum.Button_RFQ_CNC_Select_Options;
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Button_Enum.Button_RFQ_PRINTING_Select_Options;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Button_Enum.Button_RFQ_INJ_Select_Options;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Button_Enum.Button_RFQ_SHEET_METAL_Select_Options;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Button_Enum.Button_RFQ_CASTING_Select_Options;
    }
  }, [serviceType]);

  const logBatchId = useMemo(() => {
    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Button_Enum.Button_RFQ_CNC_Batch_Application_Options;
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Button_Enum.Button_RFQ_PRINTING_Batch_Application_Options;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Button_Enum.Button_RFQ_INJ_Batch_Application_Options;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Button_Enum.Button_RFQ_SHEET_METAL_Batch_Application_Options;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Button_Enum.Button_RFQ_CASTING_Batch_Application_Options;
    }
  }, [serviceType]);

  const onBatchSubmit = handleSubmit((data) => {
    for (const fileId of uploadedFilesError) {
      clearErrors(fileId);
    }

    const isEtc =
      data?.material === CapaQuotationMaterial.Etc ||
      data?.detailedMaterial === CapaQuotationDetailedMaterialType.Etc;

    const isMaterialRecommend =
      data?.material === CapaQuotationMaterial.Recommend;

    const isDetailedMaterialRecommend =
      data?.detailedMaterial === CapaQuotationDetailedMaterialType.Recommend;

    if (isRecommendServiceType) {
      handleBatchUpdateRfqFile({
        ...selectedFileInfo,
        ...data,
        material: CapaQuotationMaterial.Recommend
      });

      return onClose();
    }

    if (isEtc) {
      handleBatchUpdateRfqFile({
        ...selectedFileInfo,
        ...data,
        recommendation: undefined
      });

      return onClose();
    }

    if (isMaterialRecommend) {
      handleBatchUpdateRfqFile({
        ...selectedFileInfo,
        ...data,
        detailedMaterial: undefined,
        etc: undefined
      });

      return onClose();
    }

    if (isDetailedMaterialRecommend) {
      handleBatchUpdateRfqFile({
        ...selectedFileInfo,
        ...data,
        etc: undefined
      });

      return onClose();
    }

    handleBatchUpdateRfqFile({ ...selectedFileInfo, ...data });
    return onClose();
  });

  useLayoutEffect(() => {
    reset({ ...selectedFileInfo });
    // eslint-disable-next-line
  }, [reset]);

  return (
    <CustomizedDialog
      closeButton
      open={open}
      onClose={onClose}
      maxWidth={476}
      borderRadius={8}
      color="primary"
      icon={<Create />}
      title="가공 옵션 선택"
      actions={
        <>
          <CustomizedButton
            logId={logId}
            fullWidth
            color="primary"
            variant="contained"
            size="large"
            onClick={onSubmit}
          >
            <CustomizedTypography
              variant="body1"
              fontWeight="500"
              color="inherit"
            >
              {isMultiple ? '해당 도면만 적용하기' : '적용하기'}
            </CustomizedTypography>
          </CustomizedButton>
          {isMultiple && (
            <CustomizedTooltip
              open={!matches && toolTipOpen}
              maxWidth={337}
              color={colorSet.gray800}
              title={
                <Grid container alignItems="center">
                  <CustomizedTypography variant="caption" color="initial">
                    동일한 수량과 재료로 제작한다면
                    <br />
                    모든 도면에 수량과 재료 옵션을 한번에 적용해보세요.
                  </CustomizedTypography>
                  <IconButton onClick={onCloseToolTip}>
                    <Close size={20} color={colorSet.gray000} />
                  </IconButton>
                </Grid>
              }
              zIndex={1300}
              arrow
            >
              <CustomizedButton
                logId={logBatchId}
                variant="text"
                color="default"
                onClick={onBatchSubmit}
              >
                <CustomizedTypography variant="body2" fontWeight="500">
                  모든 도면에 동일 적용하기
                </CustomizedTypography>
              </CustomizedButton>
            </CustomizedTooltip>
          )}
        </>
      }
    >
      <Box className={classes['rfq-option-select-dialog']}>
        <Box className="option-field top">
          <CustomizedTypography
            variant="body1"
            fontWeight="500"
            color="textPrimary"
            gutterBottoms={8}
          >
            가공 옵션을 선택한 후 저장하기 버튼을 눌러주세요.
          </CustomizedTypography>
          <Box className="fileName">
            <CustomizedTypography variant="body1" color="textPrimary" noWrap>
              {selectedFileInfo?.fileName}
            </CustomizedTypography>
          </Box>
        </Box>
        {!isRecommendServiceType && (
          <Box className="option-field">
            <CustomizedTypography
              variant="body2"
              color="textPrimary"
              gutterBottoms={4}
            >
              {hasDetailedMaterial ? '재료 대분류*' : '재료*'}
            </CustomizedTypography>

            <Controller
              defaultValue={''}
              control={control}
              name="material"
              rules={{ required: true }}
              render={(render) => (
                <Select
                  {...render}
                  classes={{
                    select: classes['option-select']
                  }}
                  variant="outlined"
                  fullWidth
                  displayEmpty
                  error={errors['material'] ? true : false}
                  onChange={(e) => {
                    setValue('detailedMaterial', '');
                    setValue('etc', '');
                    return render.onChange(e);
                  }}
                >
                  <MenuItem value="" disabled>
                    {hasDetailedMaterial ? '재료 대분류' : '재료'}
                  </MenuItem>
                  {materialOptions?.map((option, index) => (
                    <MenuItem
                      classes={{ root: 'option-menu-item' }}
                      key={index}
                      value={option}
                    >
                      {requestEnumToString(option)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {errors['material'] && (
              <span className="errorMessage">
                {hasDetailedMaterial ? '재료 대분류' : '재료'}를 선택해주세요.
              </span>
            )}
          </Box>
        )}

        {hasDetailedMaterial &&
          !selectedMaterialRecommend &&
          !isRecommendServiceType && (
            <Box className="option-field">
              <CustomizedTypography
                variant="body2"
                color="textPrimary"
                gutterBottoms={4}
              >
                재료 소분류*
              </CustomizedTypography>

              <Controller
                defaultValue={''}
                control={control}
                name="detailedMaterial"
                rules={{ required: true }}
                error={errors['detailedMaterial'] ? true : false}
                render={(render) => (
                  <Select
                    {...render}
                    classes={{
                      select: classes['option-select']
                    }}
                    variant="outlined"
                    fullWidth
                    displayEmpty
                    disabled={!material}
                    onChange={(e) => {
                      setValue('etc', '');

                      return render.onChange(e);
                    }}
                  >
                    <MenuItem value="" disabled>
                      재료 소분류
                    </MenuItem>
                    {detailedMaterialOptions?.map((option, index) => (
                      <MenuItem
                        classes={{ root: 'option-menu-item' }}
                        key={index}
                        value={option ?? ''}
                      >
                        {requestEnumToString(option)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors['detailedMaterial'] && (
                <span className="errorMessage">
                  재료 소분류를 선택해주세요.
                </span>
              )}
            </Box>
          )}

        {hasEtc && (
          <Box className="option-field">
            <CustomizedTypography
              variant="body2"
              color="textPrimary"
              gutterBottoms={4}
            >
              기타 재료*
            </CustomizedTypography>

            <Controller
              control={control}
              name="etc"
              defaultValue=""
              rules={{ required: true }}
              render={(render) => (
                <OutlinedTextField
                  {...render}
                  fullWidth
                  placeholder="기타 재료를 입력해주세요"
                  align="left"
                  size="large"
                  error={errors['etc'] ? true : false}
                />
              )}
            />

            {errors['etc'] && (
              <span className="errorMessage">기타 재료를 입력해주세요.</span>
            )}
          </Box>
        )}

        {hasRecommendation && (
          <Box className="option-field">
            <CustomizedTypography variant="body2" color="textPrimary">
              재료 특성*
            </CustomizedTypography>
            <CustomizedTypography
              variant="subtitle2"
              color="textSecondary"
              gutterBottoms={8}
            >
              선택한 특성에 맞게 파트너가 재료를 추천합니다. (최대 2개 선택)
            </CustomizedTypography>

            <Controller
              control={control}
              name="recommendation"
              defaultValue={[]}
              rules={{
                validate: (value) => {
                  return value.length !== 0;
                }
              }}
              render={(render) => (
                <React.Fragment>
                  <OptionRecommendationBox
                    {...render}
                    methods={methods}
                    serviceType={serviceType}
                    detailedServiceType={detailedServiceType}
                  />

                  {(render.value as CapaQuotationRecommendation[]).includes(
                    CapaQuotationRecommendation.Etc
                  ) && (
                    <span className="errorMessage">
                      보기 이외의 특성이 필요한 경우, 상세 요청사항에
                      입력해주세요
                    </span>
                  )}

                  {(render.value as CapaQuotationRecommendation[]).includes(
                    CapaQuotationRecommendation.DecideAfterConsulting
                  ) && (
                    <span className="errorMessage">
                      상담 후 결정 선택 시 모든 특성 선택지가 비활성화 됩니다
                    </span>
                  )}

                  {errors['recommendation'] && (
                    <span className="errorMessage">
                      제조에 필요한 재료 특성을 선택해주세요.
                    </span>
                  )}
                </React.Fragment>
              )}
            />
          </Box>
        )}

        <Box className="option-field bottom">
          <CustomizedTypography
            variant="body2"
            color="textPrimary"
            gutterBottoms={4}
          >
            수량*
          </CustomizedTypography>

          <Controller
            control={control}
            name="quantity"
            defaultValue=""
            rules={{ required: true, validate: (value) => Number(value) !== 0 }}
            render={(render) => (
              <OutlinedTextField
                {...render}
                fullWidth
                placeholder="수량 입력"
                align="left"
                size="large"
                error={errors['quantity'] ? true : false}
                InputProps={{
                  inputComponent: CurrencyFormatInput
                }}
                onChange={(e) => {
                  if (e.target.value?.length > 10) {
                    return;
                  }
                  render.onChange(e.target.value as any);
                }}
              />
            )}
          />

          {errors['quantity']?.type === 'required' && (
            <span className="errorMessage">수량을 입력해주세요.</span>
          )}

          {errors['quantity']?.type === 'validate' && (
            <span className="errorMessage">
              0을 제외한 숫자만 입력해주세요.
            </span>
          )}
        </Box>
      </Box>
    </CustomizedDialog>
  );
};

export default OptionSelectDialog;
